import { useMutation, useQueryClient } from '@tanstack/react-query';
import { container } from '@src/container';
import { UniversityService } from '@src/application/universities/usecases';
import type { CreateUniversityInputDTO } from '@src/application/universities/dto/university.dto';

/**
 * Hook: useCreateUniversity
 * Creates a new university
 *
 * @param options - Mutation options
 * @returns Mutation result
 *
 * @example
 * ```typescript
 * const { mutate: createUniversity, isPending } = useCreateUniversity({
 *     onSuccess: () => {
 *         console.log('University created successfully');
 *     },
 *     onError: (error) => {
 *         console.error('Failed to create university:', error);
 *     }
 * });
 *
 * createUniversity({
 *     name: 'University Name',
 *     code: 'UNIV',
 *     description: 'University description',
 *     address: 'University address',
 *     website: 'https://university.edu'
 * });
 * ```
 */
export function useCreateUniversity(options?: {
    onSuccess?: (data: any) => void;
    onError?: (error: Error) => void;
}) {
    const queryClient = useQueryClient();
    const universityService = container.resolve<UniversityService>('UniversityService');

    return useMutation({
        mutationFn: async (input: CreateUniversityInputDTO) => {
            try {
                const result = await universityService.createUniversity(input);
                if (import.meta.env.DEV) {
                    console.log('✅ University created:', result);
                }
                return result;
            } catch (error) {
                if (import.meta.env.DEV) {
                    console.error('❌ Failed to create university:', error);
                }
                throw error;
            }
        },
        onSuccess: (data) => {
            // Invalidate and refetch universities list
            queryClient.invalidateQueries({ queryKey: ['universities'] });
            options?.onSuccess?.(data);
        },
        onError: options?.onError,
    });
}